import type {
  Recommendation,
  SoulCheckAnswers,
} from "./intake-schema";

export type Direction = Recommendation;
export type { SoulCheckAnswers };

export type DirectionSignal = Direction | "context";

export interface ScoreContribution {
  question: keyof SoulCheckAnswers;
  answer: SoulCheckAnswers[keyof SoulCheckAnswers];
  signal: DirectionSignal;
  score: 0 | 1 | 2 | null;
}

export interface DirectionResult {
  direction: Direction;
  /** The prototype-compatible sum across q1, q2 and q4 (0 through 6). */
  totalScore: number;
  /** The prototype-compatible average across q1, q2 and q4 (0 through 2). */
  averageScore: number;
  contributions: readonly ScoreContribution[];
  focusAdjusted: boolean;
}

const q1Scores: Record<SoulCheckAnswers["q1"], 0 | 1 | 2> = {
  change_stirring: 0,
  ground_shifted: 1,
  letting_go_in_between: 1,
  needs_shared_support: 2,
};

const q2Scores: Record<SoulCheckAnswers["q2"], 0 | 1 | 2> = {
  self_guided_own_pace: 0,
  understood_without_fixing: 1,
  longer_shared_process: 2,
};

const q4Scores: Record<SoulCheckAnswers["q4"], 0 | 1 | 2> = {
  solitude_own_pace: 0,
  support_soon: 1,
  ready_to_dive_together: 2,
};






function signalForScore(score: 0 | 1 | 2): Direction {
  if (score === 0) return "special";
  if (score === 1) return "heart-to-heart";
  return "the-wave";
}

function directionForBucket(bucket: number): Direction {
  if (bucket <= 0) return "special";
  if (bucket >= 2) return "the-wave";
  return "heart-to-heart";
}

/**
 * Computes the same recommendation as the original prototype, using stable
 * semantic answer codes rather than array positions.
 *
 * q1, q2 and q4 each signal a depth from 0 through 2. Their average is
 * rounded to one of the three directions. q3 is context only, except that
 * `soul_connection` moves an otherwise Special result to Heart to Heart.
 * This function is pure so the server can recompute and verify the browser's
 * result before syncing an explicitly shared form submission.
 *
 * It returns structure only. The sentences shown next to each answer and each
 * direction are editorial and live in `src/content/soul-check.ts`, which is
 * where a caller looks them up by the codes returned here.
 */
export function computeDirection(
  answers: SoulCheckAnswers,
): DirectionResult {
  const q1Score = q1Scores[answers.q1];
  const q2Score = q2Scores[answers.q2];
  const q4Score = q4Scores[answers.q4];
  const totalScore = q1Score + q2Score + q4Score;
  const averageScore = totalScore / 3;
  const originalBucket = Math.round(averageScore);
  const focusAdjusted =
    answers.q3 === "soul_connection" && originalBucket < 1;
  const bucket = focusAdjusted ? 1 : originalBucket;
  const direction = directionForBucket(bucket);

  const contributions: readonly ScoreContribution[] = [
    {
      question: "q1",
      answer: answers.q1,
      signal: signalForScore(q1Score),
      score: q1Score,
    },
    {
      question: "q2",
      answer: answers.q2,
      signal: signalForScore(q2Score),
      score: q2Score,
    },
    {
      question: "q3",
      answer: answers.q3,
      signal: answers.q3 === "soul_connection" ? "heart-to-heart" : "context",
      score: null,
    },
    {
      question: "q4",
      answer: answers.q4,
      signal: signalForScore(q4Score),
      score: q4Score,
    },
  ];

  return {
    direction,
    totalScore,
    averageScore,
    contributions,
    focusAdjusted,
  };
}
